import { Component, NgModuleRef } from "@angular/core";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { EMPTY, from, Observable, of } from "rxjs";
import { map, mergeMap, switchMap, tap, toArray } from "rxjs/operators";
import { Reference } from "src/app/inventory/reference/reference";
import { ReferenceService } from "src/app/inventory/reference/reference.service";
import { ReferenceSearchDialogComponent } from "src/app/inventory/reference/search/reference-search-dialog.component";
import { UpdateSupplierReferenceDialogComponent } from "src/app/inventory/reference/update-supplier-reference-dialog/update-supplier-reference-dialog.component";
import { NeedaConfig } from "src/app/inventory/supplier/needa-config";
import { SupplierService } from "src/app/inventory/supplier/supplier.service";
import { Page } from "src/app/util/page";
import { Invoice } from "../../invoice/invoice";
import { PurchaseOrder } from "../purchase-order";
import { PurchaseOrderDetail } from "../purchase-order-detail";
import { PurchaseOrderService } from "../purchase-order.service";

@Component({
  styleUrls: ['./purchase-order-details.component.css'],
  templateUrl: './purchase-order-details.component.html'
})
export class PurchaseOrderDetailsComponent {
  public query = "";
  public needaConfig!: NeedaConfig;
  public needaConfigMissing = false;
  public purchaseOrder!: PurchaseOrder;
  public invoices!: Invoice[];

  public loading = false;
  public searchInProgress = false;

  constructor(
    private modalService: NgbModal,
    private purchaseOrderService: PurchaseOrderService,
    private referenceService: ReferenceService,
    private router: Router,
    private supplierService: SupplierService,
    activatedRoute: ActivatedRoute,
  ) {
    activatedRoute.paramMap.pipe(
      switchMap(paramMap => this.getPurchaseOrderNumberFromParams(paramMap)),
      switchMap(purchaseOrderNumber => this.refreshPurchaseOrder(purchaseOrderNumber))
    ).subscribe();
  }

  private addReferenceToPurchaseOrder(reference: Reference) {
    this.purchaseOrderService.updateQuantity(this.purchaseOrder.number, reference.isbn, 1)
      .pipe(switchMap(() => this.refreshPurchaseOrder(this.purchaseOrder.number)))
      .subscribe();
  }

  public cancel() {
    this.purchaseOrderService.cancel(this.purchaseOrder.number)

      .subscribe(() => this.router.navigateByUrl("/purchase/order"));
  }

  public submitPurchaseOrder() {
    this.purchaseOrderService.submit(this.purchaseOrder.number)

      .subscribe(() => this.router.navigateByUrl("/purchase/order"));
  }

  public onSearchKeyup(event: KeyboardEvent) {
    if (event.key != "Enter") {
      return;
    }

    this.search();
  }

  public openSearchDialog(references: Page<Reference>) {
    let modalRef = this.modalService.open(ReferenceSearchDialogComponent, { size: 'xl' });

    var component = this;
    modalRef.componentInstance.supplier = this.purchaseOrder.supplier.name;
    modalRef.componentInstance.onAddReferences = (references: Reference[]) => from(references)
      .pipe(
        mergeMap(reference => component.purchaseOrderService.updateQuantity(this.purchaseOrder.number, reference.isbn, 1)),
        toArray(),
        switchMap(_ => component.refreshPurchaseOrder(component.purchaseOrder.number))
      );

    this.referenceService.initSearchResult({ initialResults: references, query: this.query });
  }

  private getPurchaseOrderNumberFromParams(paramMap: ParamMap): Observable<number> {
    let purchaseOrderNumber = +(paramMap.get("purchaseOrder") || "0");
    if (purchaseOrderNumber == 0) {
      this.router.navigateByUrl("/404");

      return EMPTY;
    }

    return of(purchaseOrderNumber);
  }

  private onSearchResponse(references: Page<Reference>) {
    if (references.numberOfElements == 1 && this.query != "") {
      this.addReferenceToPurchaseOrder(references.content[0]);
    } else if (references.numberOfElements > 1) {
      this.openSearchDialog(references)
    } else {
      this.referenceService.findByIsbn(this.query).subscribe({
        next: reference => this.openUpdateSupplierReferenceModal(reference),
        error: _ => this.openSearchDialog(references),
      });
    }
  }

  private openUpdateSupplierReferenceModal(reference: Reference) {
    this.query = "";
    var modalRef = this.modalService.open(UpdateSupplierReferenceDialogComponent);


    modalRef.componentInstance.reference = reference;
    modalRef.componentInstance.supplier = this.purchaseOrder.supplier;
    for (let supplierReference of reference.supplierReferences) {
      if (supplierReference.active) {
        modalRef.componentInstance.supplierReference = supplierReference;
      }
    }
    modalRef.componentInstance.onSupplierReferenceUpdate = (reference: Reference) => this.addReferenceToPurchaseOrder(reference);
  }

  private refreshNeedaConfig(purchaseOrder: PurchaseOrder) {
    this.supplierService.findNeedaConfig(purchaseOrder.supplier.id)
      .subscribe({
        next: needaConfig => this.needaConfig = needaConfig,
        error: _ => this.needaConfigMissing = true
      });
  }

  private refreshPurchaseOrder(purchaseOrderId: number): Observable<PurchaseOrder> {
    return this.purchaseOrderService.findByNumber(purchaseOrderId)

      .pipe(
        tap(purchaseOrder => this.purchaseOrder = purchaseOrder),
        tap(purchaseOrder => this.refreshNeedaConfig(purchaseOrder)),
        switchMap(_ => this.purchaseOrderService.findInvoices(this.purchaseOrder.number)),
        tap(invoices => this.invoices = invoices),
        map(_ => this.purchaseOrder),
      );
  }

  public removeDetail(detail: PurchaseOrderDetail) {
    this.loading = true;

    detail.deleting = true;

    this.purchaseOrderService.removeDetail(this.purchaseOrder.number, detail.reference.isbn)

      .pipe(switchMap(() => this.refreshPurchaseOrder(this.purchaseOrder.number)))

      .subscribe({
        error: _ => this.loading = false,
        next: _ => this.loading = false
      });
  }

  public search() {
    this.searchInProgress = true;
    this.loading = true;

    this.referenceService.search({
      query: this.query,
      author: [],
      genre: [],
      publisher: [],
      supplier: [this.purchaseOrder.supplier.name],
    }, 0, { imageMaxWidth: 35 }).subscribe({
      complete: () => {
        this.searchInProgress = false;
        this.loading = false;
      },
      error: _ => {
        this.searchInProgress = false;
        this.loading = false;
      },
      next: references => this.onSearchResponse(references)
    }
    );
  }

  public updateDeliveryMode(deliveryMode: string) {
    this.purchaseOrder.deliveryMode = deliveryMode;
    
    this.purchaseOrderService.updateDeliveryMode(deliveryMode, this.purchaseOrder.number)
      .subscribe();
  }

  public updateQuantity(detail: PurchaseOrderDetail) {
    this.purchaseOrderService.updateQuantity(this.purchaseOrder.number, detail.reference.isbn, detail.quantity)

      .subscribe();
  }
}